import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Plus, QrCode, ExternalLink, RefreshCw } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { createQRCampaign, listQRCampaigns, type QRCampaign } from '@/lib/qr-api-client';

function getDefaultCompanyId(marketingUser: any): string {
  const ids: string[] = marketingUser?.company_ids || [];
  return ids[0] || 'halesac';
}

export default function QRCampaignsPage() {
  const { marketingUser } = useAuth();

  const companyId = useMemo(() => getDefaultCompanyId(marketingUser), [marketingUser]);

  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState<QRCampaign[]>([]);
  const [showCreate, setShowCreate] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const data = await listQRCampaigns(companyId);
      setCampaigns(data);
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message || 'Failed to load QR campaigns');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  return (
    <div className="p-8">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">QR Tracking</h1>
          <p className="text-gray-600 mt-2">
            Create QR campaigns, generate unique codes, and track scans (direct mail, door hangers, yard signs).
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={load}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-2"
            disabled={loading}
          >
            <RefreshCw size={18} /> Refresh
          </button>
          <button onClick={() => setShowCreate(true)} className="btn-primary flex items-center gap-2">
            <Plus size={20} /> Create QR Campaign
          </button>
        </div>
      </div>

      {loading ? (
        <div className="card">
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading QR campaigns...</p>
            </div>
          </div>
        </div>
      ) : campaigns.length === 0 ? (
        <div className="card text-center py-12">
          <QrCode className="mx-auto text-gray-400 mb-4" size={48} />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No QR campaigns yet</h3>
          <p className="text-gray-600 mb-4">Create your first QR campaign to start tracking scans.</p>
          <button onClick={() => setShowCreate(true)} className="btn-primary">
            <Plus size={20} className="inline mr-2" /> Create QR Campaign
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {campaigns.map((c) => (
            <div key={c.id} className="card hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-gray-900">{c.name}</h3>
                    {c.tracking_domain ? (
                      <span className="text-xs px-2 py-1 rounded bg-blue-50 text-blue-700 border border-blue-100">
                        {c.tracking_domain}
                      </span>
                    ) : (
                      <span className="text-xs px-2 py-1 rounded bg-yellow-50 text-yellow-800 border border-yellow-100">
                        tracking domain not set
                      </span>
                    )}
                  </div>

                  <div className="mt-2 text-sm text-gray-600 break-all">
                    Destination: <span className="font-medium">{c.destination_url}</span>
                    <a
                      href={c.destination_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 ml-2 text-blue-600 hover:text-blue-800"
                    >
                      <ExternalLink size={14} />
                    </a>
                  </div>

                  <div className="mt-2 text-xs text-gray-500">
                    Company: <span className="font-medium">{c.company_id}</span>
                    {c.marketing_source ? (
                      <>
                        {' • '}Source: <span className="font-medium">{c.marketing_source}</span>
                      </>
                    ) : null}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Link to={`/admin/qr-tracking/${c.id}`} className="btn-primary">
                    Open
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <CreateQRCampaignModal
          companyId={companyId}
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function CreateQRCampaignModal({
  companyId,
  onClose,
  onCreated,
}: {
  companyId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState('');
  const [trackingDomain, setTrackingDomain] = useState('');
  const [destinationUrl, setDestinationUrl] = useState('');
  const [marketingSource, setMarketingSource] = useState('direct_mail');

  const [utmSource, setUtmSource] = useState('');
  const [utmMedium, setUtmMedium] = useState('');
  const [utmCampaign, setUtmCampaign] = useState('');
  const [utmContent, setUtmContent] = useState('');
  const [utmTerm, setUtmTerm] = useState('');

  const [costCents, setCostCents] = useState<number>(0);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!name.trim()) return toast.error('Name is required');
    if (!destinationUrl.trim()) return toast.error('Destination URL is required');

    try {
      setSaving(true);

      await createQRCampaign({
        name: name.trim(),
        company_id: companyId,
        destination_url: destinationUrl.trim(),
        marketing_source: marketingSource.trim(),
        tracking_domain: trackingDomain.trim() || undefined,
        utm_source: utmSource.trim() || undefined,
        utm_medium: utmMedium.trim() || undefined,
        utm_campaign: utmCampaign.trim() || undefined,
        utm_content: utmContent.trim() || undefined,
        utm_term: utmTerm.trim() || undefined,
        cost_cents: Number.isFinite(costCents) ? costCents : 0,
      });

      toast.success('QR campaign created');
      onCreated();
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message || 'Failed to create QR campaign');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full">
        <div className="p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Create QR Campaign</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input className="input w-full" value={name} onChange={(e) => setName(e.target.value)} />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
              <input
                className="input w-full"
                value={marketingSource}
                onChange={(e) => setMarketingSource(e.target.value)}
                placeholder="direct_mail | door_hanger | yard_sign | event"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Destination URL *</label>
              <input
                className="input w-full"
                value={destinationUrl}
                onChange={(e) => setDestinationUrl(e.target.value)}
                placeholder="https://..."
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Tracking Domain (recommended)</label>
              <input
                className="input w-full"
                value={trackingDomain}
                onChange={(e) => setTrackingDomain(e.target.value)}
                placeholder="memberships.halesac.com"
              />
              <p className="text-xs text-gray-500 mt-1">
                This is the branded domain your QR codes will point to (must route to the Marketing API server).
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">UTM Source</label>
              <input className="input w-full" value={utmSource} onChange={(e) => setUtmSource(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">UTM Medium</label>
              <input className="input w-full" value={utmMedium} onChange={(e) => setUtmMedium(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">UTM Campaign</label>
              <input className="input w-full" value={utmCampaign} onChange={(e) => setUtmCampaign(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">UTM Content</label>
              <input className="input w-full" value={utmContent} onChange={(e) => setUtmContent(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">UTM Term</label>
              <input className="input w-full" value={utmTerm} onChange={(e) => setUtmTerm(e.target.value)} />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Cost (cents)</label>
              <input
                type="number"
                className="input w-full"
                value={costCents}
                onChange={(e) => setCostCents(Number(e.target.value))}
                min={0}
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-6 border-t mt-6">
            <button onClick={onClose} disabled={saving} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded">
              Cancel
            </button>
            <button onClick={submit} disabled={saving} className="btn-primary">
              {saving ? 'Creating...' : 'Create Campaign'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
